/**
 * QuestionEditor — the ordered list of must-ask (or cross-cutting) questions.
 * Each question is a card: slug + question text + its multiple-choice options
 * (OptionsEditor) + allow-freeform / multi-select toggles + (v2) a phase and
 * an input-widget type. Reorder = the steps the AI walks the user through.
 * Reused for both question lists.
 */
import { INPUT_TYPES, QUESTION_PHASES, moveItem, newQuestion } from './flowModel.js'
import { OptionsEditor } from './OptionsEditor.jsx'
import { RowControls } from './RowControls.jsx'
import { FlowHelp } from './FlowHelp.jsx'

const PHASE_LABELS = { reduction: 'Reduction', requirement: 'Requirement', weighting: 'Weighting' }
const INPUT_TYPE_LABELS = { choice: 'Choice', number: 'Number', topology: 'Topology' }

// Kept out of the JSX attribute: the closing German quote after "Antwort setzt"
// is an ASCII `"`, which ends a double-quoted attribute early and turns the rest
// of the sentence into a syntax error.
export const MUST_ASK_HINT =
  'Was Ion beantwortet haben muss, bevor die Rangliste steht. Reihenfolge = ' +
  'Reihenfolge im Gespräch. Bei „Antwort setzt“ wählst du, welche Pack-Größe ' +
  'die Antwort füllt — steht dort nichts, wird der Wert beim Ranking nicht ' +
  'angewandt.'

export function QuestionEditor({
  title,
  hint,
  questions,
  onChange,
  // "Antwort setzt → …": which pack requirement this question's answer feeds.
  // It used to live in a separate "System requirements" section as a table of
  // slug → field pairs, which meant reading two places to understand one
  // question and left the mapping able to point at a slug that no longer
  // existed. Same data (`system_requirements_map`), shown where it belongs.
  requirementFields = null,
  requirementOf = null,
  onRequirementChange = null,
}) {
  const update = (i, patch) =>
    onChange(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)))

  return (
    <section className="admin-aim-section admin-flow-section">
      <div className="admin-flow-section__head">
        <h3 className="admin-aim-section-title">
          {title} {title === 'Must-ask questions' && <FlowHelp k="mustAsk" />}
        </h3>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost admin-flow-add"
          onClick={() => onChange([...questions, newQuestion()])}
        >
          + Add question
        </button>
      </div>
      {hint && <p className="admin-flow-hint">{hint}</p>}
      {questions.length === 0 && (
        <p className="admin-aim-empty admin-flow-empty">No questions yet.</p>
      )}
      <ol className="admin-flow-list admin-flow-qlist" role="list">
        {questions.map((q, i) => (
          <li key={q.id} className="admin-flow-q-card">
            <div className="admin-flow-q-head">
              <span className="admin-flow-q-num" aria-hidden="true">
                {i + 1}
              </span>
              <input
                className="admin-aim-input admin-flow-q-slug"
                type="text"
                placeholder="slug (e.g. pack_topology)"
                value={q.slug}
                aria-label={`Question ${i + 1} slug`}
                onInput={(e) => update(i, { slug: e.currentTarget.value })}
              />
              {i === 0 && <FlowHelp k="questionSlug" />}
              <RowControls
                index={i}
                count={questions.length}
                label={`question ${i + 1}`}
                onMove={(d) => onChange(moveItem(questions, i, d))}
                onRemove={() => onChange(questions.filter((_, idx) => idx !== i))}
              />
            </div>
            <textarea
              className="admin-aim-textarea admin-flow-q-text"
              rows={2}
              placeholder="Question shown to the user"
              value={q.question}
              aria-label={`Question ${i + 1} text`}
              onInput={(e) => update(i, { question: e.currentTarget.value })}
            />
            <div className="admin-flow-q-v2-row">
              <label className="admin-aim-field admin-flow-q-phase">
                <span className="admin-aim-label">
                  Phase {i === 0 && <FlowHelp k="phase" />}
                </span>
                <select
                  className="admin-aim-input"
                  value={q.phase || 'reduction'}
                  aria-label={`Question ${i + 1} phase`}
                  onChange={(e) => update(i, { phase: e.currentTarget.value })}
                >
                  {QUESTION_PHASES.map((p) => (
                    <option key={p} value={p}>
                      {PHASE_LABELS[p] || p}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-aim-field admin-flow-q-input-type">
                <span className="admin-aim-label">
                  Input {i === 0 && <FlowHelp k="inputType" />}
                </span>
                <select
                  className="admin-aim-input"
                  value={q.inputType || 'choice'}
                  aria-label={`Question ${i + 1} input type`}
                  onChange={(e) => update(i, { inputType: e.currentTarget.value })}
                >
                  {INPUT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {INPUT_TYPE_LABELS[t] || t}
                    </option>
                  ))}
                </select>
              </label>
              {requirementFields && (
                <label className="admin-aim-field admin-flow-q-req">
                  <span className="admin-aim-label">
                    Antwort setzt {i === 0 && <FlowHelp k="systemReqMapping" />}
                  </span>
                  <select
                    className="admin-aim-input"
                    value={(requirementOf && requirementOf[q.slug]) || ''}
                    aria-label={`Question ${i + 1} sets requirement`}
                    onChange={(e) =>
                      onRequirementChange?.(q.slug, e.currentTarget.value)
                    }
                  >
                    <option value="">— nichts (nur Gespräch) —</option>
                    {requirementFields.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {q.inputType === 'number' && (
                <>
                  <label className="admin-aim-field admin-flow-q-input-unit">
                    <span className="admin-aim-label">Unit</span>
                    <input
                      className="admin-aim-input"
                      type="text"
                      placeholder="e.g. V"
                      value={q.inputUnit || ''}
                      aria-label={`Question ${i + 1} input unit`}
                      onInput={(e) => update(i, { inputUnit: e.currentTarget.value })}
                    />
                  </label>
                  <label className="admin-aim-field admin-flow-q-input-bound">
                    <span className="admin-aim-label">Min</span>
                    <input
                      className="admin-aim-input"
                      type="number"
                      step="any"
                      value={q.inputMin ?? ''}
                      aria-label={`Question ${i + 1} input min`}
                      onInput={(e) => update(i, { inputMin: e.currentTarget.value })}
                    />
                  </label>
                  <label className="admin-aim-field admin-flow-q-input-bound">
                    <span className="admin-aim-label">Max</span>
                    <input
                      className="admin-aim-input"
                      type="number"
                      step="any"
                      value={q.inputMax ?? ''}
                      aria-label={`Question ${i + 1} input max`}
                      onInput={(e) => update(i, { inputMax: e.currentTarget.value })}
                    />
                  </label>
                </>
              )}
            </div>
            <div className="admin-flow-q-toggles">
              <label className="admin-aim-checkbox">
                <input
                  type="checkbox"
                  checked={q.allow_freeform !== false}
                  onChange={(e) => update(i, { allow_freeform: e.currentTarget.checked })}
                />
                <span>Allow free-text answer</span>
              </label>
              <label className="admin-aim-checkbox">
                <input
                  type="checkbox"
                  checked={!!q.multi_select}
                  onChange={(e) => update(i, { multi_select: e.currentTarget.checked })}
                />
                <span>Allow multiple selections</span>
              </label>
            </div>
            <OptionsEditor
              options={q.options}
              onChange={(opts) => update(i, { options: opts })}
            />
          </li>
        ))}
      </ol>
    </section>
  )
}
